// The Admin page's "Agent permissions" dashboard
// (docs/superpowers/specs/2026-10-15-agent-permissions-design.md, "The Admin page UI"). Doctors,
// admins and superadmins set, for a clinic schema:
// - the clinic defaults: each area Off / Read-only / Full, each send type Off / Ask first /
//   Allowed, and Auto mode on or off;
// - a person's override: the same, with "Default" (follow the clinic default) on every row; Reset
//   clears it;
// and see each agent's effective permissions and who it is shared with (its members but its owner;
// view only), and the newest changes, as sentences.
//
// With more than one clinic in reach, the picker's first choice is "All clinics" (ALL_CLINICS): one
// table of every clinic's agents, each with its clinic. Settings belong to a clinic, so that view
// changes nothing: it says to pick a clinic for that.
//
// A modal, as every other AdminPage surface (AppointmentInbox): AdminPage owns only the open flag.
// The body mounts each time the modal opens, so each opening reads afresh. Every PUT answers what
// the GET answers, and that answer replaces what is shown. One request is out at a time. The server
// checks everything again; a refusal shows its sentence.
//
// Only a person the server says is editable (editable: true) can be opened. While a change isn't
// saved, nothing that would drop it can be used (the clinic picker, another person's Edit), and
// closing asks first. While a save is out, closing waits: it can't be called back, and its answer
// (or its refusal) must land on an open page. Save, Cancel and Reset hand focus back to the
// person's Edit button.
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VisuallyHidden,
  useToast,
} from "@chakra-ui/react";
import { LockIcon } from "@chakra-ui/icons";
import PolicyGrid from "./PolicyGrid";
import {
  complete,
  historySentence,
  isCustomized,
  isObject,
  lockReason,
  modeWords,
  partialOf,
  policyErrorText,
  roleLabel,
  sameSettings,
  statusLabel,
  summary,
  typeLabel,
  whenText,
  words,
} from "./policyWords";
import {
  ALL_CLINICS,
  fetchAgentPolicy,
  saveAgentPolicyDefaults,
  saveAgentPolicyUser,
} from "../../services/agentPolicyService";

const HOLD = "Save or cancel your changes first.";
const PICK = "Pick a clinic to change its settings.";
const listOf = (value) => (Array.isArray(value) ? value.filter(isObject) : []);
const nameOf = (user) => words(user?.name) || "This person";
const agentCount = (count) => {
  const n = Number.isInteger(count) && count > 0 ? count : 0;
  if (n === 0) return "No agents";
  return n === 1 ? "1 agent" : `${n} agents`;
};
// Who an agent is shared with: its members but its owner (the server leaves the owner out), by name;
// blank when the server didn't say, so an older server's answer never reads as "Only the owner".
const sharedWith = (members) => {
  if (!Array.isArray(members)) return "";
  const names = members.filter(isObject).map((member) => words(member.name) || "Someone");
  return names.length ? names.join(", ") : "Only the owner";
};
// The server's answer, or a failure when it isn't one (nothing to show).
const answerOf = (value) => {
  if (!isObject(value)) throw new Error("");
  return value;
};

function Section({ id, title, children }) {
  return (
    <Box as="section" aria-labelledby={id}>
      <Heading as="h3" size="sm" id={id} mb={2}>
        {title}
      </Heading>
      {children}
    </Box>
  );
}

function Refusal({ children, ...props }) {
  return (
    <Alert status="error" borderRadius="md" fontSize="sm" {...props}>
      <AlertIcon />
      {children}
    </Alert>
  );
}

// The agents, view only; `withClinic`: each one's clinic first (the "All clinics" view).
function AgentsTable({ agents, withClinic = false }) {
  return (
    <TableContainer>
      <Table size="sm">
        <Thead>
          <Tr>
            {withClinic && <Th>Clinic</Th>}
            <Th>Agent</Th>
            <Th>Owner</Th>
            <Th>Shared with</Th>
            <Th>Type</Th>
            <Th>Mode</Th>
            <Th>Status</Th>
            <Th>What it can do</Th>
          </Tr>
        </Thead>
        <Tbody>
          {agents.map((agent, index) => {
            const held = !!agent.effective_mode && agent.effective_mode !== agent.mode;
            return (
              <Tr key={agent.id ?? index}>
                {withClinic && <Td>{words(agent.schema)}</Td>}
                <Td fontWeight="semibold">{words(agent.title)}</Td>
                <Td>{words(agent.owner_name)}</Td>
                <Td whiteSpace="normal">{sharedWith(agent.members)}</Td>
                <Td>{typeLabel(agent.type)}</Td>
                <Td color={held ? "orange.600" : undefined}>{modeWords(agent.mode, agent.effective_mode)}</Td>
                <Td>{statusLabel(agent.status)}</Td>
                <Td whiteSpace="normal" fontSize="xs">
                  {summary(agent.effective, { withAuto: false })}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

function PersonEditor({ id, user, draft, defaults, error, busy, saving, onDraft, onSave, onReset, onCancel }) {
  const dirty = !sameSettings(draft, user.override);
  return (
    <Box as="section" aria-labelledby={id} borderWidth="1px" borderRadius="md" p={4} my={2}>
      <Heading as="h4" size="xs" id={id} mb={1}>
        {`${nameOf(user)}'s agent permissions`}
      </Heading>
      <Text fontSize="xs" color="gray.500" mb={3}>
        Default follows the clinic default, now and when it changes.
      </Text>
      <PolicyGrid value={draft} onChange={onDraft} allowDefault defaults={defaults} isDisabled={busy} />
      {error && <Refusal mt={3}>{error}</Refusal>}
      <HStack mt={3} spacing={2} justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={busy}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onReset}
          isDisabled={busy || !isCustomized(user.override)}
          isLoading={saving === "reset"}
        >
          Reset to clinic default
        </Button>
        <Button size="sm" colorScheme="blue" onClick={onSave} isDisabled={busy || !dirty} isLoading={saving === "user"}>
          Save
        </Button>
      </HStack>
    </Box>
  );
}

function PermissionsBody({ unsavedRef, savingRef }) {
  const toast = useToast();
  const base = useId();
  const ids = {
    defaults: `${base}-defaults`,
    people: `${base}-people`,
    agents: `${base}-agents`,
    history: `${base}-history`,
    editor: `${base}-editor`,
    hold: `${base}-hold`,
  };
  const [answer, setAnswer] = useState(null); // the GET answer shown
  const [schemas, setSchemas] = useState([]); // the picker's clinics: kept while another one loads
  const [schema, setSchema] = useState(null); // the clinic shown, or asked for
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [defaultsDraft, setDefaultsDraft] = useState(null); // null: as stored
  const [defaultsError, setDefaultsError] = useState(null);
  const [editing, setEditing] = useState(null); // { id, draft, error }: the person being edited
  const [saving, setSaving] = useState(null); // "defaults" | "user" | "reset" | null
  // One request at a time: a press that lands while one is out (a fast double click) sends nothing.
  // A request still out when the modal closes lands on an unmounted body, which React ignores.
  const inFlight = useRef(false);
  // Each person's Edit button, by id, and the one to hand focus back to once its editor closes.
  const editButtons = useRef(new Map());
  const [focusRow, setFocusRow] = useState(null);

  const show = useCallback((next) => {
    setAnswer(next);
    setSchema(next.all === true ? ALL_CLINICS : words(next.schema) || null);
    setSchemas(Array.isArray(next.schemas) ? next.schemas.filter((name) => words(name)) : []);
  }, []);

  const load = useCallback(async (wanted) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setLoadError(null);
    setAnswer(null);
    setDefaultsDraft(null);
    setDefaultsError(null);
    setEditing(null);
    try {
      show(answerOf(await fetchAgentPolicy(wanted || undefined)));
    } catch (err) {
      setLoadError(policyErrorText(err));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    load(null);
  }, [load]);

  const busy = loading || saving !== null;
  const users = listOf(answer?.users);
  const agents = listOf(answer?.agents);
  const log = listOf(answer?.log);
  const schemaName = words(answer?.schema) || "this clinic";
  const storedDefaults = complete(answer?.defaults);
  const shownDefaults = defaultsDraft ?? storedDefaults;
  const defaultsDirty = defaultsDraft !== null && !sameSettings(defaultsDraft, storedDefaults);
  const editingUser = editing ? users.find((user) => user.id === editing.id) || null : null;
  const personDirty = !!editingUser && !sameSettings(editing.draft, editingUser.override);
  const unsaved = defaultsDirty || personDirty;
  // Every clinic at once: one table of their agents, and nothing to change.
  const everyClinic = answer?.all === true;
  const clinics = schema && schema !== ALL_CLINICS && !schemas.includes(schema) ? [schema, ...schemas] : schemas;

  // The modal asks before closing on unsaved changes; a closed body has none.
  useEffect(() => {
    if (unsavedRef) unsavedRef.current = unsaved;
  }, [unsaved, unsavedRef]);
  useEffect(() => () => {
    if (unsavedRef) unsavedRef.current = false;
    if (savingRef) savingRef.current = false;
  }, [unsavedRef, savingRef]);

  useEffect(() => {
    if (focusRow === null) return;
    editButtons.current.get(focusRow)?.focus();
    setFocusRow(null);
  }, [focusRow]);

  const pickSchema = (next) => {
    if (busy || unsaved || !next || next === schema) return;
    setSchema(next);
    load(next);
  };

  const saveDefaults = async () => {
    if (!answer || busy || !defaultsDirty || inFlight.current) return;
    inFlight.current = true;
    if (savingRef) savingRef.current = true;
    setSaving("defaults");
    setDefaultsError(null);
    let next = null;
    try {
      next = answerOf(await saveAgentPolicyDefaults(answer.schema, complete(shownDefaults)));
    } catch (err) {
      setDefaultsError(policyErrorText(err));
    } finally {
      inFlight.current = false;
      if (savingRef) savingRef.current = false;
      setSaving(null);
    }
    if (!next) return;
    show(next);
    setDefaultsDraft(null);
    toast({ status: "success", title: "Clinic defaults saved.", duration: 3000 });
  };

  // `settings`: the override to store, or null to clear it.
  const saveUser = async (user, settings, kind) => {
    if (busy || inFlight.current) return;
    inFlight.current = true;
    if (savingRef) savingRef.current = true;
    setSaving(kind);
    setEditing((now) => (now ? { ...now, error: null } : now));
    let next = null;
    try {
      next = answerOf(await saveAgentPolicyUser(user.id, settings));
    } catch (err) {
      setEditing((now) => (now && now.id === user.id ? { ...now, error: policyErrorText(err) } : now));
    } finally {
      inFlight.current = false;
      if (savingRef) savingRef.current = false;
      setSaving(null);
    }
    if (!next) return;
    show(next);
    setEditing(null);
    setFocusRow(user.id);
    toast({
      status: "success",
      title: settings === null ? `${nameOf(user)} now follows the clinic default.` : `Saved ${nameOf(user)}'s settings.`,
      duration: 3000,
    });
  };

  const openEditor = (user) => {
    if (busy || user.editable !== true || editing?.id === user.id || personDirty) return;
    setEditing({ id: user.id, draft: partialOf(user.override), error: null });
  };

  return (
    <Stack spacing={6}>
      <Text fontSize="sm" color="gray.600">
        Settings only narrow what agents can do: an agent never goes beyond its own user&apos;s access.
      </Text>

      {clinics.length > 1 && (
        <FormControl maxW="xs" isDisabled={busy || unsaved}>
          <FormLabel fontSize="sm" mb={1}>
            Clinic
          </FormLabel>
          <Select size="sm" value={schema ?? ""} onChange={(event) => pickSchema(event.target.value)}>
            <option value={ALL_CLINICS}>All clinics</option>
            {clinics.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
          {unsaved && <FormHelperText>{HOLD}</FormHelperText>}
        </FormControl>
      )}

      {loadError && (
        <Refusal>
          <Box flex="1">{loadError}</Box>
          <Button size="sm" ml={3} onClick={() => load(schema)}>
            Try again
          </Button>
        </Refusal>
      )}

      {loading && (
        <Flex justify="center" py={6}>
          <Spinner label="Loading agent permissions" />
        </Flex>
      )}

      {answer && everyClinic && (
        <>
          <Text fontSize="sm" color="gray.600">
            {PICK}
          </Text>
          <Section id={ids.agents} title="Agents">
            {agents.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No agents in these clinics yet.
              </Text>
            ) : (
              <AgentsTable agents={agents} withClinic />
            )}
          </Section>
        </>
      )}

      {answer && !everyClinic && (
        <>
          <Section id={ids.defaults} title="Clinic defaults">
            <Text fontSize="sm" color="gray.600" mb={3}>
              {`Applies to everyone in ${schemaName} who uses agents, unless customized below.`}
            </Text>
            <PolicyGrid value={shownDefaults} onChange={setDefaultsDraft} isDisabled={busy} />
            {defaultsError && <Refusal mt={3}>{defaultsError}</Refusal>}
            <Flex justify="flex-end" gap={2} mt={3}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDefaultsDraft(null);
                  setDefaultsError(null);
                }}
                isDisabled={busy || !defaultsDirty}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={saveDefaults}
                isDisabled={busy || !defaultsDirty}
                isLoading={saving === "defaults"}
              >
                Save clinic defaults
              </Button>
            </Flex>
          </Section>

          <Section id={ids.people} title="People">
            {personDirty && (
              <Text id={ids.hold} fontSize="xs" color="gray.500" mb={2}>
                {HOLD}
              </Text>
            )}
            {users.length === 0 ? (
              <Text fontSize="sm" color="gray.500">{`No one in ${schemaName} uses agents yet.`}</Text>
            ) : (
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Role</Th>
                      <Th>Settings</Th>
                      <Th>What their agents can do</Th>
                      <Th>Agents</Th>
                      <Th>
                        <VisuallyHidden>Edit</VisuallyHidden>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {users.map((user, index) => {
                      const locked = user.editable !== true;
                      const open = !!editingUser && editingUser.id === user.id;
                      const held = personDirty && !open; // another person's unsaved change holds this row
                      const customized = isCustomized(user.override);
                      const opens = !locked && !held;
                      return (
                        <React.Fragment key={user.id ?? index}>
                          <Tr
                            onClick={opens ? () => openEditor(user) : undefined}
                            cursor={opens ? "pointer" : undefined}
                            _hover={opens ? { bg: "blackAlpha.50" } : undefined}
                          >
                            <Td fontWeight="semibold">{nameOf(user)}</Td>
                            <Td>{roleLabel(user.role)}</Td>
                            <Td>
                              <Badge colorScheme={customized ? "purple" : "gray"}>
                                {customized ? "Customized" : "Clinic default"}
                              </Badge>
                            </Td>
                            <Td whiteSpace="normal" fontSize="xs">
                              {summary(user.effective)}
                            </Td>
                            <Td>{agentCount(user.agents)}</Td>
                            <Td whiteSpace="normal">
                              {locked ? (
                                <HStack spacing={1} color="gray.500">
                                  <LockIcon aria-hidden />
                                  <Text fontSize="xs">{lockReason(user)}</Text>
                                </HStack>
                              ) : (
                                <Button
                                  ref={(node) => {
                                    if (node) editButtons.current.set(user.id, node);
                                    else editButtons.current.delete(user.id);
                                  }}
                                  size="xs"
                                  variant="outline"
                                  isDisabled={busy || held}
                                  aria-describedby={held ? ids.hold : undefined}
                                  aria-label={`Edit ${nameOf(user)}'s agent permissions`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEditor(user);
                                  }}
                                >
                                  Edit
                                </Button>
                              )}
                            </Td>
                          </Tr>
                          {open && (
                            <Tr>
                              <Td colSpan={6} whiteSpace="normal">
                                <PersonEditor
                                  id={ids.editor}
                                  user={editingUser}
                                  draft={editing.draft}
                                  defaults={storedDefaults}
                                  error={editing.error}
                                  busy={busy}
                                  saving={saving}
                                  onDraft={(draft) => setEditing((now) => (now ? { ...now, draft } : now))}
                                  onSave={() =>
                                    saveUser(editingUser, isCustomized(editing.draft) ? partialOf(editing.draft) : null, "user")
                                  }
                                  onReset={() => saveUser(editingUser, null, "reset")}
                                  onCancel={() => {
                                    setEditing(null);
                                    setFocusRow(editingUser.id);
                                  }}
                                />
                              </Td>
                            </Tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Section>

          <Section id={ids.agents} title="Agents">
            {agents.length === 0 ? (
              <Text fontSize="sm" color="gray.500">{`No agents in ${schemaName} yet.`}</Text>
            ) : (
              <>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  Change these through the owner&apos;s settings above.
                </Text>
                <AgentsTable agents={agents} />
              </>
            )}
          </Section>

          <Section id={ids.history} title="History">
            {log.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No changes yet.
              </Text>
            ) : (
              <List spacing={2}>
                {log.map((entry, index) => {
                  const when = whenText(entry.at);
                  return (
                    <ListItem key={index} fontSize="sm">
                      <Text>{historySentence(entry)}</Text>
                      {when && (
                        <Text fontSize="xs" color="gray.500">
                          {when}
                        </Text>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Section>
        </>
      )}
    </Stack>
  );
}

export default function AgentPermissionsModal({ isOpen, onClose }) {
  // Whether the body holds a change nobody saved: closing (the X, Esc, a click outside) asks first.
  // Whether a save is out: closing waits for it.
  const unsaved = useRef(false);
  const saving = useRef(false);
  const close = () => {
    if (saving.current) return;
    if (unsaved.current && !window.confirm("Discard your changes?")) return;
    onClose?.();
  };
  return (
    <Modal isOpen={isOpen} onClose={close} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Agent permissions</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <PermissionsBody unsavedRef={unsaved} savingRef={saving} />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
